import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 재시도 상태 추적
  const retryCountRef = React.useRef(0);
  const retryTimerRef = React.useRef(null);
  const MAX_RETRIES = 3;

  // 사용자 정보 로드 함수 (실패 시 자동 재시도)
  const fetchUserInfo = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        console.log('인증 토큰이 없습니다.');
        setUser(null);
        setPermissions([]);
        setLoading(false);
        retryCountRef.current = 0;
        return;
      }

      const response = await fetch('/api/user/me', {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // 431 오류 (Request Header Fields Too Large) 처리
        if (response.status === 431) {
          console.error('요청 헤더가 너무 큽니다. 토큰을 재발급 받아야 할 수 있습니다.');
          localStorage.removeItem('authToken');
          retryCountRef.current = 0;
          throw new Error('인증 토큰이 너무 큽니다. 다시 로그인해주세요.');
        }
        throw new Error(`사용자 정보를 불러오는데 실패했습니다. (${response.status})`);
      }

      const data = await response.json();

      if (data.success) {
        setUser(data.user);
        setPermissions(data.permissions || []);
        setGroups(data.groups || []);
        setBrands(data.brands || []);
        retryCountRef.current = 0; // 성공 시 재시도 카운터 초기화
        console.log('[UserContext] 사용자 정보 로드 완료');
        console.log('[UserContext] user:', data.user);
        console.log('[UserContext] user.brdCd:', data.user?.brdCd);
        console.log('[UserContext] groups:', data.groups);
        console.log('[UserContext] permissions:', data.permissions);
        console.log('[UserContext] brands:', data.brands?.length ? data.brands : `없음 → user.brdCd: ${data.user?.brdCd}`);
      } else {
        throw new Error(data.message || '사용자 정보를 불러오는데 실패했습니다.');
      }
    } catch (err) {
      console.error('사용자 정보 로드 오류:', err);
      setError(err.message);

      // ⚡ 자동 재시도 (최대 3회, 지수 백오프)
      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current += 1;
        const delay = Math.min(1000 * Math.pow(2, retryCountRef.current - 1), 5000);
        console.log(`🔄 사용자 정보 재시도 ${retryCountRef.current}/${MAX_RETRIES} (${delay}ms 후)`);

        if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = setTimeout(() => {
          fetchUserInfo();
        }, delay);
        return; // 재시도 중에는 user/permissions를 초기화하지 않음
      }

      // 재시도 실패 시 초기화
      console.error(`❌ 사용자 정보 로드 ${MAX_RETRIES}회 재시도 후 실패`);
      setUser(null);
      setPermissions([]);
      setGroups([]);
      setBrands([]);
      retryCountRef.current = 0;
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 로그아웃 함수
  const logout = () => {
    localStorage.removeItem('authToken');
    setUser(null);
    setPermissions([]);
    setGroups([]);
    setBrands([]);
    window.location.href = '/login';
  };

  // 특정 권한 체크 함수
  const hasPermission = (resourceId, action = null, brdCd = null) => {
    // admin(group_id=1)은 모든 권한 pass
    if (groups && groups.includes(1)) return true;

    if (!permissions || permissions.length === 0) {
      return false;
    }

    return permissions.some(perm => {
      const resourceMatch = perm.resource_id === resourceId;
      const actionMatch = action ? perm.action === action : true;
      // brd_cd가 null이면 모든 브랜드에 적용, 값이 있으면 현재 브랜드와 일치해야 함
      const brandMatch = !perm.brd_cd || !brdCd || perm.brd_cd === brdCd;
      return resourceMatch && actionMatch && brandMatch;
    });
  };

  // 초기 로드 (빈 의존성 배열로 마운트 시 한 번만 실행)
  useEffect(() => {
    const authToken = localStorage.getItem('authToken');
    if (authToken) {
      fetchUserInfo();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 빈 배열: 컴포넌트 마운트 시 한 번만 실행

  const value = {
    user,
    permissions,
    groups,
    brands,
    loading,
    error,
    fetchUserInfo,
    logout,
    hasPermission
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// Custom Hook
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === null) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
